import React, { FC, useEffect, useRef, useState } from 'react';
import { Button, useTheme } from '@mui/material';
import toast from 'react-hot-toast';
import { ISubProfession } from '../../models/Profession.interface';
import axiosInstant from '../../api';
import { SingleSubProfessionEndpoint, adminSubProfessionAttributeValues } from '../../api/endpoints';
import { DotLottleLoader } from '../../components/Loader';
import {
  ActionIconButton,
  FlashWrapper,
  InlineInput,
  SubLine,
} from './constants';
import { ConfirmationDialog } from '../../components/Modal/ConfirmationDialog';
import { IconTrash, IconPencil, IconConfirm, IconClose, IconSettings } from '../../utils/icons';
import { AttributeOptionsModal } from '../../components/AttributeOptions/AttributeOptionsModal';

interface Props {
  subProfession: ISubProfession;
  parentColor?: string;
  onUpdate(subProfessionId: number, newItem: ISubProfession | null): void;
  isNew?: boolean;
}

export const SubProfessionListItem: FC<Props> = ({
  subProfession,
  parentColor = '#D5D0C8',
  onUpdate,
  isNew,
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attrsOpen, setAttrsOpen] = useState(false);
  const [newName, setNewName] = useState(subProfession.name);
  const [flash, setFlash] = useState<'' | 'green' | 'red'>('');

  useEffect(() => {
    if (isNew) {
      setFlash('green');
      setTimeout(() => setFlash(''), theme.transitions.duration.standard * 2);
    }
  }, []);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const handleDeleteSubProfession = async () => {
    setLoading(true);
    try {
      await axiosInstant.delete(SingleSubProfessionEndpoint(subProfession.id));
      setDeleting(false);
      toast.success(`Sub-profession '${subProfession.name}' Removed!`);
      setTimeout(() => onUpdate(subProfession.id, null), 100);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
      setFlash('red');
    }
  };

  const handleNameSave = async () => {
    setLoading(true);
    try {
      const response = await axiosInstant.patch(SingleSubProfessionEndpoint(subProfession.id), { name: newName });
      onUpdate(subProfession.id, response.data);
      toast.success('Profession successfully updated!');
      setEditing(false);
      setFlash('green');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update');
      setFlash('red');
    }
    setTimeout(() => setFlash(''), theme.transitions.duration.standard);
    setLoading(false);
  };

  const handleNameCancel = () => {
    setEditing(false);
    setNewName(subProfession.name);
  };

  return (
    <>
      <FlashWrapper flash={flash}>
        <div className="flex items-center gap-2.5 pl-7 pr-4 py-2 sm:pl-4 sm:pr-3 sm:py-1.5">
          <SubLine color={parentColor} />

          {editing ? (
            <InlineInput
              ref={inputRef}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameSave();
                if (e.key === 'Escape') handleNameCancel();
              }}
            />
          ) : (
            <span className="flex-1 min-w-0 truncate text-sm text-text-1">{subProfession.name}</span>
          )}

          <div className="flex items-center gap-0.5 ml-auto shrink-0">
            {!loading && !editing && (
              <>
                <ActionIconButton title="Attribute options" onClick={() => setAttrsOpen(true)}>
                  <IconSettings width={14} height={14} stroke="currentColor" />
                </ActionIconButton>
                <ActionIconButton title="Edit" onClick={() => setEditing(true)}>
                  <IconPencil size={13} stroke="currentColor" />
                </ActionIconButton>
                <ActionIconButton title="Delete" onClick={() => setDeleting(true)}>
                  <IconTrash width={13} height={13} stroke="currentColor" />
                </ActionIconButton>
              </>
            )}
            {!loading && editing && (
              <>
                <ActionIconButton tone="confirm" title="Save" onClick={handleNameSave}>
                  <IconConfirm size={13} />
                </ActionIconButton>
                <ActionIconButton title="Cancel" onClick={handleNameCancel}>
                  <IconClose size={13} />
                </ActionIconButton>
              </>
            )}
            {loading && <DotLottleLoader sx={{ width: 28, height: 28 }} fill={false} />}
          </div>
        </div>
      </FlashWrapper>

      <ConfirmationDialog
        title="Delete Sub-Profession"
        description={`Are you sure you want to delete '${subProfession.name}'? This action cannot be undone.`}
        show={deleting}
        onConfirm={handleDeleteSubProfession}
        onCancel={() => setDeleting(false)}
        confirmButton={({ onClick }) => (
          <Button color="red" startIcon={<IconTrash width={24} height={24} stroke={theme.palette.common.white} />} onClick={onClick}>
            Delete Sub-Profession
          </Button>
        )}
      />

      {attrsOpen && (
        <AttributeOptionsModal
          title={`Attribute options — ${subProfession.name}`}
          endpoint={adminSubProfessionAttributeValues(subProfession.id)}
          onClose={() => setAttrsOpen(false)}
        />
      )}
    </>
  );
};
